import { cur } from '@/lib/currency';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MessageCircle, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Dashboard widget — Smart Dunning scorecard.
 * Pure-presentation extraction; UI and behaviour identical to the previous inline markup.
 */
export default function DunningScorecardWidget({ dunningScore }) {
  const navigate = useNavigate();

  return (
    <Card className="border-border border-error/30 bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-900/20 dark:to-pink-900/20 dark:border-rose-700" data-testid="dunning-scorecard-widget">
      <CardHeader className="pb-2">
        <CardTitle className="font-outfit text-base flex items-center gap-2">
          <div className="w-8 h-8 bg-error/20 rounded-lg flex items-center justify-center">
            <MessageCircle size={16} className="text-error dark:text-rose-400" />
          </div>
          <span>Dunning Scorecard</span>
          <Badge className="ml-auto bg-error/20 text-error dark:bg-rose-900/50 dark:text-rose-300 text-[10px]">
            Last 90 days
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {dunningScore && dunningScore.reminders_sent > 0 ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-white/70 dark:bg-muted/70 rounded-lg border border-error/30 dark:border-rose-800">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Success Rate</p>
                <p className="text-3xl font-bold font-outfit text-error dark:text-rose-300">{dunningScore.success_rate}%</p>
                <p className="text-[10px] text-muted-foreground">{dunningScore.paid_after} of {dunningScore.reminders_sent} settled</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Cured</p>
                <p className="text-lg font-bold text-success">{cur()} {dunningScore.total_cured_amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                {dunningScore.avg_days_to_pay !== null && (
                  <p className="text-[10px] text-muted-foreground">Avg {dunningScore.avg_days_to_pay}d to pay</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 bg-success/10 dark:bg-emerald-900/30 rounded-lg border border-success/30 dark:border-emerald-800 text-center">
                <p className="text-[10px] text-muted-foreground">Friendly</p>
                <p className="text-lg font-bold text-success">{dunningScore.tier_counts?.friendly || 0}</p>
              </div>
              <div className="p-2 bg-warning/10 dark:bg-amber-900/30 rounded-lg border border-warning/30 dark:border-amber-800 text-center">
                <p className="text-[10px] text-muted-foreground">Firm</p>
                <p className="text-lg font-bold text-warning">{dunningScore.tier_counts?.firm || 0}</p>
              </div>
              <div className="p-2 bg-error/10 dark:bg-rose-900/30 rounded-lg border border-error/30 dark:border-rose-800 text-center">
                <p className="text-[10px] text-muted-foreground">Final</p>
                <p className="text-lg font-bold text-error">{dunningScore.tier_counts?.final || 0}</p>
              </div>
            </div>
            {dunningScore.top_responders?.length > 0 && (
              <div>
                <p className="text-[10px] text-muted-foreground mb-1.5 font-medium">Top Responders</p>
                {dunningScore.top_responders.slice(0, 3).map((r, i) => (
                  <div key={i} className="flex justify-between items-center py-1 border-b border-error/30 dark:border-rose-800 last:border-0" data-testid={`dunning-responder-${i}`}>
                    <span className="text-xs font-medium truncate flex-1">{r.customer_name}</span>
                    <span className="text-xs font-bold text-success ml-2">{cur()} {r.amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  </div>
                ))}
              </div>
            )}
            <Button variant="outline" className="w-full text-xs h-8" onClick={() => navigate('/smart-dunning')} data-testid="dunning-go-btn">
              <ChevronRight size={12} className="mr-1" />Open Smart Dunning
            </Button>
          </div>
        ) : (
          <div className="text-center py-4 text-muted-foreground">
            <MessageCircle size={32} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm font-medium">No reminders sent yet</p>
            <p className="text-xs mt-1 mb-3">Outstanding: <span className="font-bold text-error">{cur()} {(dunningScore?.outstanding_now || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></p>
            <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => navigate('/smart-dunning')} data-testid="dunning-start-btn">
              Start Smart Dunning
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
